import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-progesterone-check"),
  kind: "test",
  tags: ["lab", "her", "during-cycle", "testing", "ivf", "iui", "fet"],
  title: "Progesterone",
  subtitle: "Blood draw to confirm progesterone levels for cycle support",
  body: {
    testName: "Progesterone Level Blood Draw",
    category: "blood-marker",
    whatItMeasures:
      "Measures serum progesterone, the hormone essential for preparing the uterine lining for implantation and sustaining early pregnancy. It is checked to confirm ovulation, verify supplementation levels, or assess luteal phase support.",
    howItWorks:
      "A small blood sample is drawn from a vein in your arm. The lab measures the progesterone concentration in nanograms per milliliter (ng/mL). Typical phase-specific ranges: follicular phase 0.1–1.5 ng/mL, ovulation 1–3 ng/mL, mid-luteal (7 days post-ovulation) 5–20 ng/mL, postmenopausal <0.5 ng/mL. A level above 3 ng/mL generally confirms ovulation occurred. ASRM 2021 explicitly states no single mid-luteal progesterone value reliably diagnoses luteal phase deficiency - progesterone is pulsatile and oscillates 5–40 ng/mL within hours in normally ovulating women. During a medicated FET cycle, your clinic may check progesterone the day before or the day of transfer to confirm your lining has been adequately exposed (~10 ng/mL is a commonly used adequacy threshold). If levels are low, your doctor may increase your progesterone dose (vaginal, intramuscular, or oral) or delay the transfer. Take your progesterone supplements as prescribed. Do not skip a dose before the test. Note the time of your last progesterone dose, as your clinic may ask. Morning draws are typically preferred for consistency.",
    quickFacts: [
      { kind: "sample", value: "Blood" },
      { kind: "time", value: "5 min draw" },
      { kind: "pain", value: "Needle pinch" },
      { kind: "prep", value: "Do not skip prescribed progesterone" },
      { kind: "results", value: "Same day, often a few hours" },
    ],
    turnaroundTime: "Same day (a few hours)",
    costRange: { low: 30, high: 100 },
    atHome: false,
    clinicRequired: true,
    cycleDay:
      "About 7 days after ovulation for natural-cycle confirmation; protocol-specific during FET or stimulation.",
    markerTiming: [
      {
        marker: "Progesterone",
        timing:
          "About 7 days after ovulation for ovulation confirmation; protocol-specific during FET or stimulation",
        reason:
          "Progesterone is low before ovulation and rises afterward, so a day-21 test only makes sense for someone who ovulates around day 14.",
      },
    ],
    dataPoints: [makeId("dp-progesterone-level")],
    section: "cycle-monitoring",
  },
  sources: [
    {
      title:
        "Diagnosis and treatment of luteal phase deficiency: a committee opinion",
      authors:
        "Practice Committee of the American Society for Reproductive Medicine",
      journal: "Fertility and Sterility",
      year: 2021,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/diagnosis-and-treatment-of-luteal-phase-deciency-a-committee-opinion-2021/",
      evidenceLevel: "guideline",
    },
    {
      title: "Luteal phase support for assisted reproduction cycles",
      authors:
        "van der Linden M, Buckingham K, Farquhar C, Kremer JAM, Metwally M",
      journal: "Cochrane Database of Systematic Reviews",
      year: 2015,
      url: "https://www.cochrane.org/CD009154/MENSTR_luteal-phase-support-assisted-reproduction-cycles",
      evidenceLevel: "meta-analysis",
    },
  ],
  readTimeMinutes: 3,
};

registerBlock(test);
